import { memo } from 'react';

import { Button } from '../../designSystem/Button';
import { cn } from '../../lib/styles';

export interface SectionPlaceholderProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  onActionClick?: () => void;
  actionSubtitle?: React.ReactNode;
  className?: string;
  fullScreen?: boolean;
}

const SectionPlaceholderTitle = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="text-text-primary font-heading-xl capitalize font-semibold text-center dark:text-white">
      {children}
    </div>
  );
};

const SectionPlaceholderDescription = ({ children }: { children: React.ReactNode }) => {
  return (
    <div
      className="text-text-primary font-body-md font-medium text-center dark:text-white/80"
      style={{ textTransform: 'none', fontWeight: 400 }}
    >
      {children}
    </div>
  );
};

const SectionPlaceholderAction = ({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) => {
  return (
    <Button size="lg" className="w-full" onClick={onClick}>
      {children}
    </Button>
  );
};

const SectionPlaceholderActionSubtitle = ({ children }: { children: React.ReactNode }) => {
  return <div className="text-text-primary font-body-md">{children}</div>;
};

const SectionPlaceholderBase = ({
  icon,
  title,
  description,
  action,
  onActionClick,
  actionSubtitle,
  fullScreen,
  className,
}: SectionPlaceholderProps) => {
  return (
    <div
      className={cn(
        'flex flex-col gap-12 items-center justify-center px-16 py-14',
        cn(
          {
            'h-full w-full': fullScreen,
          },
          className,
        ),
      )}
    >
      <div className="flex flex-col gap-2 items-center">
        {icon && <div className="flex items-center justify-center">{icon}</div>}

        {title && <SectionPlaceholderTitle>{title}</SectionPlaceholderTitle>}

        {description && <SectionPlaceholderDescription>{description}</SectionPlaceholderDescription>}
      </div>

      {action && (
        <div className="flex flex-col items-center gap-6 w-full">
          {typeof action !== 'object' ? (
            <SectionPlaceholderAction onClick={onActionClick}>{action}</SectionPlaceholderAction>
          ) : (
            action
          )}

          {actionSubtitle && <SectionPlaceholderActionSubtitle>{actionSubtitle}</SectionPlaceholderActionSubtitle>}
        </div>
      )}
    </div>
  );
};

export const SectionPlaceholder = memo(SectionPlaceholderBase);
